import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

class Tabs {
  #tabsContainer;
  #tabsNavigationArrowPrev;
  #tabsNavigationArrowNext;
  #hideClass = 'swiper-button-lock';

  constructor(tabsContainer) {
    this.#tabsContainer = tabsContainer;
  }

  setDssyButtonTabAttributes(tab) {
    const tabDssyButton = tab.shadowRoot.querySelector('button');

    if (tabDssyButton) {
      tabDssyButton.setAttribute('aria-selected', tab.dataset.ariaSelected);
      tabDssyButton.setAttribute('tabindex', tab.dataset.tabindex);
      tabDssyButton.setAttribute('role', tab.dataset.role);

      if ('ariaControlsElements' in Element.prototype) {
        const tabPanel = document.getElementById(tab.dataset.ariaControls);

        if (tabPanel) {
          tabDssyButton.ariaControlsElements = [tabPanel];
        }
      }
    }
  }

  setDssyButtonSliderNavAttributes(sliderNav) {
    const sliderNavDssyButton = sliderNav.shadowRoot.querySelector('button');

    if (sliderNavDssyButton) {
      sliderNavDssyButton.setAttribute('role', sliderNav.dataset.role);

      if ('ariaControlsElements' in Element.prototype) {
        const tabList = document.getElementById(sliderNav.dataset.ariaControls);

        if (tabList) {
          sliderNavDssyButton.ariaControlsElements = [tabList];
        }
      }
    }
  }

  setEventListener(triggerSwiper, targetSwiper, onclick = false) {
    if (typeof triggerSwiper !== 'undefined' && typeof targetSwiper !== 'undefined') {
      const tabs = this.#tabsContainer.querySelectorAll('.ec-tabs__tab-label');

      triggerSwiper.on('slideChange', swiperEvent => {
        tabs.forEach((tab, index) => {
          if (index === targetSwiper.activeIndex) {
            tab.setAttribute('data-aria-selected', 'true');
            tab.setAttribute('data-tabindex', '0');
            tab.removeAttribute('inverted');
          } else {
            tab.setAttribute('data-aria-selected', 'false');
            tab.setAttribute('data-tabindex', '-1');
            tab.setAttribute('inverted', '');
          }

          this.setDssyButtonTabAttributes(tab);
        });

        this.handleAriaDisabledForNavigationButtons();
      });

      if (onclick) {
        triggerSwiper.on('keyPress', (swiperEvent, keyCode) => {
          if (keyCode === 'Enter' || keyCode === ' ') {
            tabs.forEach((tab, index) => {
              if (tab.parentElement.classList.contains('swiper-slide-active')) {
                targetSwiper.slideTo(index);
                tab.shadowRoot.querySelector('button').focus();
              }
            });
          }

          if (keyCode === 'ArrowRight') {
            if (swiperEvent.realIndex < tabs.length - 1) {
              triggerSwiper.slideTo(swiperEvent.realIndex + 1, 50);

              setTimeout(() => {
                tabs[triggerSwiper.realIndex].shadowRoot.querySelector('button').focus();
              }, 60);
            }
          }

          if (keyCode === 'ArrowLeft') {
            if (swiperEvent.realIndex > 0) {
              triggerSwiper.slideTo(swiperEvent.realIndex - 1, 50);

              setTimeout(() => {
                tabs[triggerSwiper.realIndex].shadowRoot.querySelector('button').focus();
              }, 60);
            }
          }
        });
      } else {
        triggerSwiper.on('slideChange', swiperEvent => {
          const tabPanels = this.#tabsContainer.querySelectorAll('.ec-tab[role="tabpanel"]');

          tabPanels.forEach((tabPanel, index) => {
            if (index === swiperEvent.realIndex) {
              tabPanel.setAttribute('aria-hidden', 'false');
            } else {
              tabPanel.setAttribute('aria-hidden', 'true');
            }
          });

          if (targetSwiper.activeIndex !== swiperEvent.realIndex) {
            targetSwiper.slideTo(swiperEvent.realIndex);
          }
        });

        triggerSwiper.on('click', swiperEvent => {
          setTimeout(() => {
            triggerSwiper.updateAutoHeight();
          }, 100);
        });
      }
    }
  }

  addNavHideClass(navButton) {
    if (!navButton.classList.contains(this.#hideClass)) {
      navButton.classList.add(this.#hideClass);
    }
  }

  removeNavHideClasse(navButton) {
    if (navButton.classList.contains(this.#hideClass)) {
      navButton.classList.remove(this.#hideClass);
    }
  }

  setNavAsDisabled(navButton) {
    navButton.setAttribute('disabled', '');
    navButton.removeAttribute('inverted');
  }

  setNavAsEnabled(navButton) {
    navButton.removeAttribute('disabled');
    navButton.setAttribute('inverted', '');
  }

  handleAriaDisabledForNavigationButtons() {
    if (this.#tabsNavigationArrowPrev !== null) {
      const dssyNavButton = this.#tabsNavigationArrowPrev.shadowRoot.querySelector('button');
      const isDisabled = dssyNavButton.getAttribute('disabled') !== null;
      const isHidden = dssyNavButton.classList.contains(this.#hideClass);

      if (isDisabled) {
        this.setNavAsDisabled(this.#tabsNavigationArrowPrev);
      } else {
        this.setNavAsEnabled(this.#tabsNavigationArrowPrev);
      }

      if (isHidden) {
        this.addNavHideClass(this.#tabsNavigationArrowPrev);
      } else {
        this.removeNavHideClasse(this.#tabsNavigationArrowPrev);
      }
    }

    if (this.#tabsNavigationArrowNext !== null) {
      const dssyNavButton = this.#tabsNavigationArrowNext.shadowRoot.querySelector('button');
      const isDisabled = dssyNavButton.getAttribute('disabled') !== null;
      const isHidden = dssyNavButton.classList.contains(this.#hideClass);

      if (isDisabled) {
        this.setNavAsDisabled(this.#tabsNavigationArrowNext);
      } else {
        this.setNavAsEnabled(this.#tabsNavigationArrowNext);
      }

      if (isHidden) {
        this.addNavHideClass(this.#tabsNavigationArrowNext);
      } else {
        this.removeNavHideClasse(this.#tabsNavigationArrowNext);
      }
    }
  }

  init() {
    const tabs = this.#tabsContainer.querySelector('.ec-tabs__tabs');
    const contents = this.#tabsContainer.querySelector('.ec-tabs__tab-contents');
    let tabsSwiper, contentsSwiper, contentsBlockSizes = [];
    this.#tabsNavigationArrowPrev = this.#tabsContainer.querySelector('.ec-tabs__prev');
    this.#tabsNavigationArrowNext = this.#tabsContainer.querySelector('.ec-tabs__next');
    const buttons = this.#tabsContainer.querySelectorAll('.ec-tabs__tab-label');

    this.setDssyButtonSliderNavAttributes(this.#tabsNavigationArrowNext);
    this.setDssyButtonSliderNavAttributes(this.#tabsNavigationArrowPrev);

    if (tabs !== null) {
      tabsSwiper = new Swiper(tabs, {
        modules: [ Navigation ],
        slidesPerView: 1,
        normalizeSlideIndex: false,
        speed: 50,
        navigation: {
          nextEl: this.#tabsNavigationArrowNext.shadowRoot.querySelector('button'),
          prevEl: this.#tabsNavigationArrowPrev.shadowRoot.querySelector('button'),
        },
        breakpoints: {
          1280: {
            slidesPerView: 'auto'
          }
        }
      });

      if (buttons.length > 0) {
        buttons.forEach((button, index) => {
          this.setDssyButtonTabAttributes(button);

          button.addEventListener('keyup', evt => {
            if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'ArrowRight' || evt.key === 'ArrowLeft') {
              tabsSwiper.emit('keyPress', evt.key);
              return;
            }

            if (evt.key !== 'Tab') {
              return;
            }

            const buttonCBR = button.parentElement.getBoundingClientRect();
            const tabsCBR = tabs.getBoundingClientRect();
            const tabsSwiperWrapper = tabs.querySelector('.swiper-wrapper');
            let targetTransform = 0;
            let tabsSwiperWrapperTransformLeft = 0;
            let changeTransform = false;
            const tabsSwiperWrapperTransform = tabsSwiperWrapper.style.transform.match(/(translate3d\()(.*?)(px.*?\))/);

            if (tabsSwiperWrapperTransform !== null) {
              if (tabsSwiperWrapperTransform.length >= 4) {
                tabsSwiperWrapperTransformLeft = parseInt(tabsSwiperWrapperTransform[2]);

                if (buttonCBR.left < tabsCBR.left) {
                  targetTransform = tabsSwiperWrapperTransformLeft + tabsCBR.left - buttonCBR.left;
                  changeTransform = true;
                } else if (buttonCBR.right > tabsCBR.right) {
                  targetTransform = tabsSwiperWrapperTransformLeft + tabsCBR.right - buttonCBR.right;
                  changeTransform = true;
                }

                if (changeTransform) {
                  tabsSwiperWrapper.style.transform = `${tabsSwiperWrapperTransform[1]}${targetTransform}${tabsSwiperWrapperTransform[3]}`;
                }
              } else {
                if (buttonCBR.left < tabsCBR.left) {
                  targetTransform = tabsCBR.left - buttonCBR.left;
                  changeTransform = true;
                } else if (buttonCBR.right > tabsCBR.right) {
                  targetTransform = tabsCBR.right - buttonCBR.right;
                  changeTransform = true;
                }

                if (changeTransform) {
                  tabsSwiperWrapper.style.transform = `translate3d(${targetTransform}px,0px,0px)`;
                }
              }
            } else {
              if (buttonCBR.left < tabsCBR.left) {
                targetTransform = tabsCBR.left - buttonCBR.left;
                changeTransform = true;
              } else if (buttonCBR.right > tabsCBR.right) {
                targetTransform = tabsCBR.right - buttonCBR.right;
                changeTransform = true;
              }

              if (changeTransform) {
                tabsSwiperWrapper.style.transform = `translate3d(${targetTransform}px,0px,0px)`;
              }
            }

            tabsSwiper.slideTo(index);
          });
        });
      }

      if (this.#tabsNavigationArrowPrev !== null) {
        this.#tabsNavigationArrowPrev.addEventListener('keydown', evt => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
          }

          if (evt.key === 'Tab' && !evt.shiftKey) {
            evt.preventDefault();

            buttons.forEach((button, index) => {
              if (button.parentElement.classList.contains('swiper-slide-active')) {
                tabsSwiper.slideTo(index);
                button.shadowRoot.querySelector('button').focus();
              }
            });
          }
        });

        this.#tabsNavigationArrowPrev.addEventListener('keyup', evt => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();

            if (tabsSwiper.realIndex > 0) {
              tabsSwiper.slidePrev(50);

              setTimeout(() => {
                if (this.#tabsNavigationArrowPrev.getAttribute('disabled') !== null) {
                  buttons[tabsSwiper.realIndex].shadowRoot.querySelector('button').focus();
                }
              }, 60);
            }
          }
        });
      }

      if (this.#tabsNavigationArrowNext !== null) {
        this.#tabsNavigationArrowNext.addEventListener('keydown', evt => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
          }

          if (evt.key === 'Tab' && evt.shiftKey) {
            evt.preventDefault();

            buttons.forEach((button, index) => {
              if (button.parentElement.classList.contains('swiper-slide-active')) {
                tabsSwiper.slideTo(index);
                button.shadowRoot.querySelector('button').focus();
              }
            });
          }
        });

        this.#tabsNavigationArrowNext.addEventListener('keyup', evt => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();

            if (tabsSwiper.realIndex < buttons.length - 1) {
              tabsSwiper.slideNext(50);

              setTimeout(() => {
                if (this.#tabsNavigationArrowNext.getAttribute('disabled') !== null) {
                  buttons[tabsSwiper.realIndex].shadowRoot.querySelector('button').focus();
                }
              }, 60);
            }
          }
        });
      }
    }

    const resizeObserver = new ResizeObserver((entries) => {
      let hasChanges = false;

      entries.forEach((entry, index) => {
        if (entry.contentBoxSize) {
          if (typeof contentsBlockSizes[index] === 'undefined') {
            contentsBlockSizes[index] = entry.contentBoxSize.blockSize;
          } else {
            if (contentsBlockSizes[index] !== entry.contentBoxSize.blockSize) {
              contentsBlockSizes[index] = entry.contentBoxSize.blockSize;
              hasChanges = true;
            }
          }
        }
      });

      if (hasChanges) {
        contentsSwiper.updateAutoHeight();
      }
    });

    const that = this;

    if (contents !== null) {
      contentsSwiper = new Swiper(contents, {
        autoHeight: true,
        slidesPerView: 1,
        observer: true,
        speed: 50,
        on: {
          'afterInit': () => {
            const tabs = contents.querySelectorAll('.ec-tab');

            tabs.forEach(tab => {
              resizeObserver.observe(tab);
            });

            that.handleAriaDisabledForNavigationButtons();
          },
          'resize': () => {
            that.handleAriaDisabledForNavigationButtons();
          }
        }
      });

      if (buttons.length > 0) {
        buttons.forEach((button, index) => {
          that.setDssyButtonTabAttributes(button);

          button.addEventListener('clickEvent', () => {
            tabsSwiper.slideTo(index);
            contentsSwiper.slideTo(index);
            button.shadowRoot.querySelector('button').focus();
          });
        });
      }
    }

    this.setEventListener(tabsSwiper, contentsSwiper, true);
    this.setEventListener(contentsSwiper, tabsSwiper);
  }
}

const tabsElements = document.querySelectorAll('[data-js-block="tabs"]');

if (tabsElements.length > 0) {
  tabsElements.forEach(tabsElement => {
    const tabsElementClass = new Tabs(tabsElement);
    tabsElementClass.init();
  });
}

export default Tabs;
